/**
 * `usePaginatedQuery` (plan D4) — one hook over the uniform `{ data, meta }`.
 *
 * `page` and `pageSize` live in the **URL**, not in component state. A support
 * conversation that goes "it's on page 4" has to survive being pasted into a
 * chat window, and a list that resets to page 1 on every re-render is the most
 * reliably irritating bug in an admin panel.
 */

import type { Paginated } from '@rd/shared';
import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

import { apiPaginated } from './apiClient.js';

export const DEFAULT_PAGE_SIZE = 25;

export interface PaginatedQueryArgs<T> {
  key: readonly unknown[];
  path: string;
  query?: Record<string, string | number | boolean | undefined>;
  enabled?: boolean;
  /**
   * Passed through to `useQuery` untouched, so a list can poll itself — the
   * jobs list refreshes while any row is still running. TanStack's own type,
   * not a local `number`: the function form (deciding from the last response)
   * is precisely the shape `useJob` established, and retyping it here would
   * drift from it.
   */
  refetchInterval?: UseQueryOptions<Paginated<T>>['refetchInterval'];
}

export type PaginatedQueryResult<T> = UseQueryResult<Paginated<T>, unknown> & {
  page: number;
  pageSize: number;
  setPage(n: number): void;
};

export function usePaginatedQuery<T>(args: PaginatedQueryArgs<T>): PaginatedQueryResult<T> {
  const [params, setParams] = useSearchParams();

  const page = clampInt(params.get('page'), 1);
  const pageSize = clampInt(params.get('pageSize'), DEFAULT_PAGE_SIZE);

  const setPage = useCallback(
    (next: number) => {
      const updated = new URLSearchParams(params);
      updated.set('page', String(Math.max(1, next)));
      // `replace` so paging does not fill the back button with every page the
      // user skimmed past.
      setParams(updated, { replace: true });
    },
    [params, setParams],
  );

  const result = useQuery<Paginated<T>>({
    queryKey: [...args.key, page, pageSize, args.query],
    queryFn: () => apiPaginated<T>(args.path, { query: { ...args.query, page, pageSize } }),
    enabled: args.enabled ?? true,
    // Spread-when-set rather than `refetchInterval: args.refetchInterval`:
    // `exactOptionalPropertyTypes` distinguishes an absent property from an
    // explicit `undefined`, and TanStack's option type only accepts the former.
    ...(args.refetchInterval === undefined ? {} : { refetchInterval: args.refetchInterval }),
  });

  return Object.assign(result, { page, pageSize, setPage });
}

function clampInt(raw: string | null, fallback: number): number {
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 ? value : fallback;
}
